export interface Mp4Box {
  type: string
  /** Byte offset of the box header in the file. */
  offset: number
  /** Total box size including the header, clamped to the file length. */
  size: number
  headerSize: number
}

const MAX_BOXES = 64
const HEADER_READ = 16

/**
 * Walk the top-level boxes of an MP4 using small File.slice() reads only.
 * A file that does not start with an ftyp box yields []. Truncated or garbage input never throws.
 */
export async function readTopLevelBoxes(file: File): Promise<Mp4Box[]> {
  const boxes: Mp4Box[] = []
  let offset = 0
  while (offset + 8 <= file.size && boxes.length < MAX_BOXES) {
    const header = await readBytes(file, offset, HEADER_READ)
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength)
    const type = latin1(header.subarray(4, 8))
    if (boxes.length === 0 && type !== 'ftyp') return []

    let size = view.getUint32(0)
    let headerSize = 8
    if (size === 1) {
      if (header.length < 16) break
      size = view.getUint32(8) * 2 ** 32 + view.getUint32(12)
      headerSize = 16
    } else if (size === 0) {
      size = file.size - offset
    }
    if (size < headerSize) break

    size = Math.min(size, file.size - offset)
    boxes.push({ type, offset, size, headerSize })
    offset += size
  }
  return boxes
}

/** The payload of a box (after its header) as latin1 text, at most maxBytes long. */
export async function readBoxText(file: File, box: Mp4Box, maxBytes: number): Promise<string> {
  const length = Math.min(box.size - box.headerSize, maxBytes)
  if (length <= 0) return ''
  return latin1(await readBytes(file, box.offset + box.headerSize, length))
}

async function readBytes(file: File, start: number, length: number): Promise<Uint8Array> {
  // FileReader rather than Blob.arrayBuffer(): older WebViews and jsdom lack the latter.
  const blob = file.slice(start, start + length)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

function latin1(bytes: Uint8Array): string {
  return new TextDecoder('latin1').decode(bytes)
}
